import {
    AggregateFunctions,
    AggregateQueryInterval,
    AggregateQueryMode,
    ApiQuery,
    ApiQueryExample,
    Filter,
    FilterOperators,
    SortDirections,
} from "~/modules/filters/types";
import {
    TokenTimeseriesColumns,
    TokenTimeseriesGroupBy,
} from "~/modules/token_timeseries/types";

// // // //

// NOTE - without a date filter the flows cover the whole history, so every
// default + example query is bounded to a window.
const YEAR_TO_DATE_FILTER: Filter = {
    field: TokenTimeseriesColumns.date,
    operator: FilterOperators.OnOrAfter,
    value: "2026-01-01T05:00:00.000Z",
};

const SEPTEMBER_2026_FILTER: Filter = {
    operator: FilterOperators.And,
    filters: [
        {
            field: TokenTimeseriesColumns.date,
            operator: FilterOperators.OnOrAfter,
            value: "2026-09-04T04:00:00.000Z",
        },
        {
            field: TokenTimeseriesColumns.date,
            operator: FilterOperators.OnOrBefore,
            value: "2026-10-04T04:00:00.000Z",
        },
    ],
};

/**
 * DEFAULT_FUND_FLOWS_QUERY
 * Default ApiQuery for the fund flows API
 */
export const DEFAULT_FUND_FLOWS_QUERY: ApiQuery = {
    filter: {
        operator: FilterOperators.And,
        filters: [YEAR_TO_DATE_FILTER],
    },
    aggregate: {
        groupBy: TokenTimeseriesGroupBy.asset,
        aggregateFunction: AggregateFunctions.Sum,
        mode: AggregateQueryMode.flow,
        groupLimit: 100,
    },
    sort: {
        direction: SortDirections.asc,
        field: TokenTimeseriesColumns.date,
    },
    pagination: {
        page: 1,
        perPage: 25,
    },
};

/**
 * DEFAULT_FUND_FLOWS_TIMESERIES_QUERY
 * Default ApiQuery for the fund flows timeseries API
 */
export const DEFAULT_FUND_FLOWS_TIMESERIES_QUERY: ApiQuery = {
    ...DEFAULT_FUND_FLOWS_QUERY,
    aggregate: {
        groupBy: TokenTimeseriesGroupBy.network,
        aggregateFunction: AggregateFunctions.Sum,
        mode: AggregateQueryMode.flow,
        interval: AggregateQueryInterval.week,
    },
};

// // // //

const topAssetsSeptember2026: ApiQueryExample = {
    key: "topAssetsSeptember2026",
    content: {
        label: "Top 10 Assets - Sep 2026",
        description:
            "The ten assets with the largest net fund flows over September 2026.",
    },
    query: {
        filter: SEPTEMBER_2026_FILTER,
        aggregate: {
            groupBy: TokenTimeseriesGroupBy.asset,
            aggregateFunction: AggregateFunctions.Sum,
            groupLimit: 10,
        },
        sort: {
            direction: SortDirections.asc,
            field: TokenTimeseriesColumns.date,
        },
        pagination: {
            page: 1,
            perPage: 25,
        },
    },
};

const assetClassesYearToDate: ApiQueryExample = {
    key: "assetClassesYearToDate",
    content: {
        label: "Asset Classes - 2026 YTD",
        description: "Compare net fund flows by asset class over 2026.",
    },
    query: {
        filter: {
            operator: FilterOperators.And,
            filters: [YEAR_TO_DATE_FILTER],
        },
        aggregate: {
            groupBy: TokenTimeseriesGroupBy.assetClass,
            aggregateFunction: AggregateFunctions.Sum,
        },
        sort: {
            direction: SortDirections.asc,
            field: TokenTimeseriesColumns.date,
        },
        pagination: {
            page: 1,
            perPage: 25,
        },
    },
};

const weeklyNetworksSeptember2026: ApiQueryExample = {
    key: "weeklyNetworksSeptember2026",
    content: {
        label: "Weekly Flows by Network - Sep 2026",
        description:
            "Weekly net fund flows for each network over September 2026.",
    },
    query: {
        filter: SEPTEMBER_2026_FILTER,
        aggregate: {
            groupBy: TokenTimeseriesGroupBy.network,
            aggregateFunction: AggregateFunctions.Sum,
            interval: AggregateQueryInterval.week,
        },
        sort: {
            direction: SortDirections.asc,
            field: TokenTimeseriesColumns.date,
        },
        pagination: {
            page: 1,
            perPage: 25,
        },
    },
};

/**
 * FUND_FLOWS_API_QUERY_EXAMPLES
 * Example queries for the fund flows API
 */
export const FUND_FLOWS_API_QUERY_EXAMPLES: ApiQueryExample[] = [
    topAssetsSeptember2026,
    assetClassesYearToDate,
];

/**
 * FUND_FLOWS_TIMESERIES_API_QUERY_EXAMPLES
 * Example queries for the fund flows timeseries API
 */
export const FUND_FLOWS_TIMESERIES_API_QUERY_EXAMPLES: ApiQueryExample[] = [
    weeklyNetworksSeptember2026,
    assetClassesYearToDate,
];
